import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Globe, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { Logo } from "@/components/Logo";

const ITEMS = [["home", "/"], ["about", "/about"], ["services", "/services"], ["locations", "/locations"], ["sectors", "/sectors"], ["contact", "/contact"]];

export const Header = () => {
  const { t, lang, lp, switchPath } = useLang();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);

  const solid = scrolled || open;
  const linkCls = ({ isActive }) =>
    `nav-link relative py-2 text-sm font-medium transition-colors duration-300 ${solid ? "text-[#1E3A5F]" : "text-white"} ${isActive ? "is-active" : ""}`;

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${solid ? "border-b border-[#8EA3BC]/30 bg-white/95 backdrop-blur-md" : "border-b border-white/10 bg-transparent"}`}>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8 lg:px-12">
        <Link to={lp("/")} data-testid="header-logo-link" className="shrink-0">
          <Logo light={!solid} lang={lang} testId="header-logo-placeholder" label={t.common.logoPlaceholder} />
        </Link>
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {ITEMS.map(([k, p]) => (
            <NavLink key={k} to={lp(p)} end={p === "/"} className={linkCls} data-testid={`nav-${k}`}>{t.nav[k]}</NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to={switchPath} data-testid="lang-switcher" lang={lang === "ar" ? "en" : "ar"}
            className={`inline-flex items-center gap-2 border px-3 py-2 text-sm font-medium transition-colors duration-300 ${solid ? "border-[#8EA3BC]/50 text-[#1E3A5F] hover:border-[#1E3A5F]" : "border-white/30 text-white hover:border-white"}`}>
            <Globe className="h-4 w-4" aria-hidden="true" />
            <span className={lang === "ar" ? "font-sans" : "font-arabic"}>{t.langLabel}</span>
          </Link>
          <Link to={lp("/quote")} data-testid="header-quote-button" className="btn-brass hidden md:inline-flex">
            {t.nav.quote}<ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" aria-hidden="true" />
          </Link>
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-expanded={open} aria-label={open ? t.nav.close : t.nav.menu}
            className={`inline-flex h-10 w-10 items-center justify-center border lg:hidden ${solid ? "border-[#8EA3BC]/50 text-[#1E3A5F]" : "border-white/30 text-white"}`}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#8EA3BC]/30 bg-white lg:hidden" data-testid="mobile-menu" aria-label="Mobile">
            <ul className="px-5 py-4 sm:px-8">
              {ITEMS.map(([k, p]) => (
                <li key={k} className="border-b border-[#8EA3BC]/25">
                  <NavLink to={lp(p)} end={p === "/"} data-testid={`mobile-nav-${k}`} className="block py-4 font-serif text-2xl text-[#1E3A5F]">{t.nav[k]}</NavLink>
                </li>
              ))}
              <li className="pt-5">
                <Link to={lp("/quote")} data-testid="mobile-quote-button" className="btn-brass w-full justify-center">{t.nav.quote}</Link>
              </li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
